import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { IStore, IPagedResults, IPagedResult, IFacilityInfo, IStoreGroup, IPTIFacilityModel} from '../Shared/interface';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AppInsightsService } from './app-insights.service';
import { IStorageFacilityList } from '../Shared/interface2';
import {AppService} from './app.service';
import { UserSessionService } from './usersession.service';
import { SwtIndexedDbService } from '../add-ons/sitewalkthrough/services/swt-indexed-db.service';

@Injectable({
    providedIn: 'root',
})

export class StoreService {

    private baseURL = this.appService.config.apiBaseUrl;
    store: IStore[] = [];
    storeGroup: IStoreGroup[] = [];

    /**
     * In-memory cache of the scanner type configured for the currently active facility.
     * 0 = not loaded / not configured; 1–3 = valid scanner type (RFID, QR, Bluetooth).
     * Populated by SiteWalkthroughComponent on store select and after saving scanner settings.
     */
    configuredScannerTypeId: number = 0;

    constructor(private http: HttpClient,
                private appinsights: AppInsightsService,
                private appService: AppService,
                private userSessionService: UserSessionService,
                private idbService: SwtIndexedDbService) { }

    getStoresWithGenericAndRegion(): Observable<IStore[]> {
        return this.http.get<IStore[]>(
            `${this.baseURL}/store/GetStores` )
            .pipe(
                map(store => {
                    return store as IStore[];

                }),

                catchError(this.handleError)
            );
    }

    getStores(): Observable<IStore[]> {
        return this.http.get<IStore[]>(
            `${this.baseURL}/store/GetStores` )
            .pipe(
                map(stores => {
                    stores as IStore[];
                    if (stores && stores.length) {
                        stores.map(st => {
                            st.GenericStoreGroups = [];
                        });
                        this.userSessionService.allStoresListFromGetStores = stores as any;
                        return stores;
                    } else {
                        return stores;
                    }

                }),

                catchError(this.handleError)
            );
  }

    getStoreGroup(): Observable<any> {
        return this.http.get<any>(
            `${this.baseURL}/StoreGroup/GetStoreGroups` )
            .pipe(
                map(storeGroup => {
                    return storeGroup;

                }),
                catchError(this.handleError)
            );
    }
    getUsers(): Observable<any> {
        return this.http.get<any>(
            `${this.baseURL}/User/GetUsersActiveOrInactive` )
            .pipe(
                map( User => {
                    return User;

                }),

                catchError(this.handleError)
            );
    }

    // getStores(id: number): Observable<IStore> {
    //     return this.http.get<IStore>(this.baseURL + '/' + id)
    //         .pipe(
    //             map(store => {
    //                 return store;
    //             }),
    //             catchError(this.handleError)
    //         );
    // }

    getStoresPage(page: number, pageSize: number): Observable<IPagedResults<IStore[]>> {
        return this.http.get<IPagedResult>(
            `${this.baseURL}/store/GetStores?page=${page}&pageSize=${pageSize}`,
            { observe: 'response' })
            .pipe(
                map(res => {
                    const pagedResult = res.body as IPagedResult;
                    return {
                        Results: pagedResult.List as IStore[],
                        TotalRecords: pagedResult.TotalRecords
                    };
                }),
                catchError(this.handleError)
            );
    }

    GetLocationList(): Observable<IStorageFacilityList[]> {
        return this.http.get<IStorageFacilityList[]>(
            `${this.baseURL}/store/GetLocationList` )
            .pipe(
                map(store => {
                    return store as IStorageFacilityList[];

                }),

                catchError(this.handleError)
            );
    }

    getPTIStores(): Observable<IPTIFacilityModel[]>{
        return this.http.get<IPTIFacilityModel[]>(
            `${this.baseURL}/store/GetPTIStores`)
            .pipe(
                map(res=>{
                    return res as IPTIFacilityModel[];
                }),
                catchError(this.handleError)
            );
    }

    /**
     * Returns the scanner type configured for the facility.
     * - Online:  calls the API, persists the result to IDB (swt_settings)
     *            and updates the in-memory cache.
     * - Offline: reads from IDB (swt_settings) as fallback.
     * Always returns 0 when not configured (null / 0 in DB).
     */
    getScannerType(facilityId: number): Observable<number> {
        const idbKey = `scanner_type_facility_${facilityId}`;
        if (navigator.onLine) {
            return this.http.get<number>(
                `${this.baseURL}/SWT/GetScannerType?facilityId=${facilityId}`
            ).pipe(
                map(typeId => (typeId >= 854 && typeId <= 856) ? typeId : 0),
                tap(typeId => {
                    // Persist to IDB for offline use.
                    this.idbService.put('swt_settings', { key: idbKey, value: typeId }).catch(() => {});
                    this.configuredScannerTypeId = typeId;
                }),
                catchError(this.handleError),
            );
        }
        // Offline — read from IDB.
        return from(
            this.idbService.get<{ key: string; value: number }>('swt_settings', idbKey)
                .then(rec => {
                    const typeId = (rec && rec.value >= 854 && rec.value <= 856) ? rec.value : 0;
                    this.configuredScannerTypeId = typeId;
                    return typeId;
                })
        );
    }

    private handleError(error: HttpErrorResponse) {
        console.log('server error:', error);
        if (error.error instanceof Error) {
            const errMessage = error.error.message;
            this.appinsights.logEvent(errMessage);
            return throwError(errMessage);
        }
        return throwError(error || 'Node.js server error');
  }

  getStoresForBulkAttributes(): Observable<IStore[]> {
    return this.http.get<IStore[]>(
      `${this.baseURL}/store/GetStoresForBulkAttributes`)
      .pipe(
        map(stores => {
          stores as IStore[];
          if (stores && stores.length) {
            stores.map(st => {
              st.GenericStoreGroups = [];
            });
            this.userSessionService.allStoresListFromGetStores = stores as any;
            return stores;
          } else {
            return stores;
          }

        }),

        catchError(this.handleError)
      );
  }
}

